import { getPreferredPhone, useGetAllContacts } from "@api/contactService";
import { useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectInlineEdit from "@components/objects/ObjectInlineEdit";
import { useCreatePublicImprovement, useDeletePublicImprovement, useGetAllPublicImprovements, useUpdatePublicImprovement } from "@api/publicImprovementService";
import { formatCurrency } from "@utils/numbers";
import { formatDate, formatSeconds } from "@utils/dates";
import { useCreateMembeshipLevel, useDeleteMembeshipLevel, useGetAllMembeshipLevels, useUpdateMembeshipLevel } from "@api/membershipLevelService";
import { useCreateTraining, useDeleteTraining, useGetAllTrainings, useUpdateTraining } from "@api/trainingService";
import { useCreateAppeal, useDeleteAppeal, useGetAllAppeals, useUpdateAppeal } from "@api/appealService";
import { useCreateFund, useDeleteFund, useGetAllFunds, useUpdateFund } from "@api/fundService";
import { useCreateRegistry, useDeleteRegistry, useGetAllRegistries, useUpdateRegistry } from "@api/registriesService";
import { useCreateZoning, useDeleteZoning, useGetAllZonings, useUpdateZoning } from "@api/zoningService";
import { useCreateSsa, useDeleteSsa, useGetAllSsas, useUpdateSsa } from "@api/ssaService";
import { useCreateTif, useDeleteTif, useGetAllTifs, useUpdateTif } from "@api/tifService";
import { importStatuses, importTypes, tallySkipped, useDeleteImport, useGetAllImports } from "@api/importService";
import { FauxTable, FauxTableRow } from "@components/FauxTable";

const columns = [
    {
        id: 'id',
        numeric: false,
        disablePadding: true,
        sortable: false,
        label: 'File',
        cell: (row, {LinkComponent}) => {
            // const LinkComponent = view === 'deleted' ? Box : Link;
            return (<>
                <Typography sx={{display: 'inline-block'}}>
                    <LinkComponent to={`/tools/importers/${row.id}`}>
                        <Typography fontWeight={'bold'}>
                            {importTypes?.[row.type] ?? 'N/A'}
                        </Typography>
                    {row.files.length > 0 && (
                        <Box sx={{display: 'block'}}>{row.files[row.files.length - 1].name}</Box>
                    )}
                    </LinkComponent>
                </Typography>
            </>)
        }
      },
      {
        id: 'created_at',
        numeric: false,
        disablePadding: true,
        sortable: false,
        label: 'Date',
        cell: (row, {LinkComponent}) => {
            // const LinkComponent = view === 'deleted' ? Box : Link;
            return (<>
                {formatDate(row.created_at)}
            </>)
        }
      },
      {
        id: 'status',
        numeric: false,
        disablePadding: true,
        sortable: false,
        label: 'Date',
        cell: (row, {LinkComponent}) => {
            // const LinkComponent = view === 'deleted' ? Box : Link;
            return (<>
                {importStatuses?.[row.status] ?? 'N/A'}
            </>)
        }
      },
      {
        id: 'statistics',
        numeric: false,
        disablePadding: true,
        sortable: false,
        label: 'Report',
        cell: (row, {LinkComponent}) => {
            // const LinkComponent = view === 'deleted' ? Box : Link;
            if(!row.statistics) {
                return 'N/A';
            }

            return (<>
                <FauxTable>
                    <FauxTableRow title={'Duration'}>{formatSeconds(row.statistics.duration)}</FauxTableRow>
                    {(row?.statistics?.imported?.contacts?.length ?? 0) > 0 && <FauxTableRow title={'Contacts'}>{row.statistics.imported.contacts.length}</FauxTableRow>}
                    {(row?.statistics?.imported?.businesses?.length ?? 0) > 0 && <FauxTableRow title={'Businesses'}>{row.statistics.imported.businesses.length}</FauxTableRow>}
                    {(row?.statistics?.imported?.properties?.length ?? 0) > 0 && <FauxTableRow title={'Properties'}>{row.statistics.imported.properties.length}</FauxTableRow>}
                    {(!!row?.statistics?.imported.workplan) && <FauxTableRow title={'Work Plan'} breakTitle={false}>Yes</FauxTableRow>}
                    {tallySkipped(row) > 0 && ( <FauxTableRow title={'Skipped'}>{tallySkipped(row)}</FauxTableRow>)}
                    {Object.keys(row?.statistics.failed ?? {}).length > 0 && ( <FauxTableRow title={'Failed'}>{Object.keys(row?.statistics.failed ?? {}).length}</FauxTableRow>)}
                </FauxTable>
            </>)
        }
      }
];

const listCell = (row, {LinkComponent}) => {
    return (<>
    {row.name !== row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.name}
                </LinkComponent>
            </Typography>
            {row.company_name && (<Typography fontSize="sm">{row.company_name}</Typography>)}
        </Box>
    )}
    {row.name === row.company_name && (
        <Box sx={{mb: 1}}>
            <Typography>
                <LinkComponent to={`/contacts/${row.id}`} object={row}>
                    {row.company_name}
                </LinkComponent>
            </Typography>
        </Box>
        )}
    </>);
};

const listActions = (row, {view, LinkComponent, ViewAction}) => {
    if(view === 'deleted') {
        return null;
    }

    return (<>
        <Tooltip title="Email">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!row.email}
                component={'a'}
                href={`mailto:${row.email}`}
                >
                <EmailRoundedIcon />
            </IconButton>
        </Tooltip>
        <Tooltip title="Call">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{ bgcolor: 'background.surface', ":disabled": {opacity: 0.3} }}
                disabled={!getPreferredPhone(row)}
                component={'a'}
                href={`tel:${getPreferredPhone(row)}`}
                >
                <LocalPhoneRoundedIcon />
            </IconButton>
        </Tooltip>
    </>);
};

export default function ImportIndex() {
    return (
        <ObjectIndex
            defaultParams={{orderby: 'created_at', order: 'desc'}}
            query={useGetAllImports}
            deleteMutation={useDeleteImport}
            // createMutation={useCreateTif}
            // updateMutation={useUpdateTif}
            // deleteMutation={useDeleteTif}

            type={'import'}
            endpoint={'imports'}
            permissionType={'settings'}
            title={'Import History'}
            color={'default'}
            columns={columns}
            disableActions={true}
            listCell={listCell}
            listActions={listActions}
        />
    )
}
